import React, { memo, useContext, useState, useMemo } from 'react'
import { TextField } from 'transfix-ui/components/TextField'
import { Box, makeStyles } from 'transfix-ui/core'
import {
  FeatureFlagsContext,
  FEATURES_DEFAULTS,
  sourceType,
} from 'utils/context/FeatureFlagsContext'

const useStyles = makeStyles(theme => ({
  input: {
    position: 'fixed',
    zIndex: 99,
    paddingRight: theme.spacing(1.5),
    [theme.breakpoints.up('sm')]: {
      width: '60%',
      paddingRight: '30px',
    },
    [theme.breakpoints.between('sm', 'lg')]: {
      width: '90%',
    },
    [theme.breakpoints.up('md')]: {
      width: '40%',
      paddingRight: '20px',
    },
  },
  flagItem: {
    '& div': {
      marginRight: '10px',
    },
  },
  divider: {
    marginTop: theme.spacing(1.25),
  },
  flex: {
    display: 'flex',
  },
  enabled: {
    color: 'green',
  },
  disabled: {
    color: 'red',
  },
}))

interface IFeatureFlagRow {
  name: string
  enabled: boolean
  source: sourceType
}

const FeatureFlagRow = memo<IFeatureFlagRow>(({ enabled, name, source }) => {
  const styles = useStyles()

  return (
    <li className={styles.flagItem}>
      <div className={styles.flex}>
        <div>
          <strong>{name}</strong>
        </div>
        <div className={enabled ? styles.enabled : styles.disabled}>
          {enabled ? 'enabled' : 'disabled'}
        </div>
        <div>{source}</div>
      </div>
    </li>
  )
})

export const FeatureFlags = memo(() => {
  const styles = useStyles()
  const { flags } = useContext(FeatureFlagsContext)
  const flagKeys = useMemo(() => Object.keys(FEATURES_DEFAULTS), [])
  const [displayedFlags, setDisplayedFlags] = useState<string[]>(flagKeys)

  const filterChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.currentTarget.value === '') {
      setDisplayedFlags(flagKeys)
    } else {
      const filteredTables = displayedFlags.filter(flagKey =>
        flagKey.toLowerCase().includes(event.currentTarget.value.toLowerCase())
      )
      setDisplayedFlags(filteredTables)
    }
  }

  return (
    <>
      <TextField
        className={styles.input}
        placeholder='Search flags…'
        onChange={filterChangeHandler}
        type='search'
      />
      <Box display='flex' flexDirection='column' marginTop={2}>
        <div>
          <ol>
            {displayedFlags.map(k => {
              const { enabled, source } = flags[k as keyof typeof flags]
              return <FeatureFlagRow name={k} enabled={enabled} source={source} key={k} />
            })}
          </ol>
        </div>
      </Box>
    </>
  )
})
